import * as React from 'react';
import axios from 'axios';
import IReactEvent from 'PyzShopUi/scripts/interfaces/article-list/IReactEvent';
import { MediaCenterEvent } from 'PyzShopUi/scripts/react/common/events/mediaCenterEvent';
import MiddlewareEventBus from 'PyzShopUi/scripts/react/common/lib/MiddlewareEventBus';
import { useTranslationHook } from 'PyzShopUi/scripts/utils/translationHook';
import i18next from 'i18next';
import {
    mapBikeSearchResponseProductsToBikes
} from 'PyzShopUi/scripts/product-detail-page/includes/bikeSearchRepsonseMapper';
import {
    BikeListItem,
    IBikeSearchResponse,
    IBikeSearchResponseBike
} from 'PyzShopUi/scripts/product-detail-page/interfaces';

interface IProductBikeSearchSuggestionProps {
    searchValue?: string;
    onClickHandler?: (selectedConcreteId: number, bike?: any) => void;
    onClose?: () => void;
    isVisible?: boolean;
    onSelectBike?: () => void;
    abstractSku: string;
    productBikeSearchApiUrl?: string;
}

export const getBikeDisplayName = (bike: IBikeSearchResponseBike): string => {
    return `${bike?.name}${bike?.manufacture_year_start ? `, ${i18next.t('Year of manuf.', { ns: 'article-detail' })} ${bike?.manufacture_year_start}` : ''} ${bike?.model ? `(${bike.model})` : ''}`;
};

const ProductBikeSearchSuggestion: React.FunctionComponent<IProductBikeSearchSuggestionProps> = (props: IProductBikeSearchSuggestionProps) => {
    const {
        searchValue,
        onClickHandler,
        onClose,
        isVisible,
        onSelectBike,
        abstractSku,
        productBikeSearchApiUrl
    }: IProductBikeSearchSuggestionProps = props;

    const [matchingBikeList, setMatchingBikeList] = React.useState<BikeListItem[]>();
    const [notMatchingBikeList, setNotMatchingBikeList] = React.useState<any>(null);
    const [activeBikeCode, setActiveBikeCode] = React.useState<string>('');
    const { translate } = useTranslationHook();

    React.useEffect(() => {
        if (!isVisible) {
            return;
        }

        setActiveBikeCode('');
        axios.get(productBikeSearchApiUrl, {
            params: { 'searchTerm': searchValue, 'abstractSku': abstractSku }
        }).then((response): void => {
            const bikeSearchResponse: IBikeSearchResponse = { ...response.data };
            if (!bikeSearchResponse) {
                return;
            }

            const matchingBikesList: BikeListItem[] = mapBikeSearchResponseProductsToBikes(bikeSearchResponse.products);
            setMatchingBikeList(matchingBikesList);
            setNotMatchingBikeList(bikeSearchResponse.bikes_without_product_relation);
        });
    }, [searchValue, isVisible]);

    if (!isVisible || !matchingBikeList) {
        return null;
    }

    if (!matchingBikeList?.length) {
        return (
            <>
                <div className="bike-list-header heading4">
                    0 {translate('Bikes', {ns:'search'})}
                </div>
            </>
        );
    }

    const getBikeListItem = (bike, concreteId: number) =>
        (
            <li key={getBikeDisplayName(bike)} onClick={() => {
                const filterSlidesFunction = (swiperSlide: HTMLElement) => concreteId === parseInt(swiperSlide.dataset.idConcrete, 10);
                const mediaCenterRenderFilteredSlidesEvent: IReactEvent<any> = {
                    type: MediaCenterEvent.RENDER_FILTERED_SLIDES,
                    payload: filterSlidesFunction
                };
                MiddlewareEventBus.getInstance().publish(mediaCenterRenderFilteredSlidesEvent);
                setActiveBikeCode(bike?.bike_code);
                onClickHandler(concreteId, bike);
                onClose();
                onSelectBike();
            }} className={bike?.bike_code === activeBikeCode ? 'active' : ''}
                data-qa="product-configurator-bike-list-element">
                {getBikeDisplayName(bike)}
                <i className="icon icon-check"></i>
            </li>
        );

    return (
        <>
            <div className="bike-list-header heading4">
                {translate('Matching variant', { ns: 'article-detail' })}:
            </div>
            <div>
                <ul className="matching-bikes">
                    {matchingBikeList.map((bike) => {
                        return getBikeListItem(bike, bike?.products?.[0]?.concrete_id);
                    })}
                </ul>
            </div>
            {notMatchingBikeList?.length ?
                <>
                    <div className="bike-list-header heading4">
                        {translate('Not matching variant', { ns: 'article-detail' })}:
                    </div>
                    <div>
                        <ul className="not-matching-bikes">
                            {notMatchingBikeList?.map((bike) =>
                                <li key={getBikeDisplayName(bike)}>
                                    {getBikeDisplayName(bike)}
                                    <i className="icon icon-close"></i>
                                </li>
                            )}
                        </ul>
                    </div>
                </> : null
            }
        </>
    );
};

ProductBikeSearchSuggestion.defaultProps = {
    searchValue: ''
};

export default ProductBikeSearchSuggestion;
